import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    CircularProgress,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";

import { useEffect, useState } from "react";
import { EntityId } from "@reduxjs/toolkit";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsKabaddiIcon from "@mui/icons-material/SportsKabaddi";
import SickIcon from "@mui/icons-material/Sick";

import { DateTime } from "luxon";

import { useGetIncidentsQuery } from "services/streamingApi/caiPortal.api";
import { IncidentType } from "types/Incident";

import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { IncidentViewerIncidentPanel } from "./IncidentViewerIncidentPanel";

export interface IncidentViewerProps {
    siteId: EntityId;
    incidentTime: string;
    incidentsPollingIntervalInMs: number;
}

export function IncidentViewer(props: IncidentViewerProps) {
    const [selectedIncidentId, setSelectedIncidentId] = useState<
        EntityId | undefined
    >(undefined);

    const { data, error, isLoading, isFetching, isSuccess, isError } =
        useGetIncidentsQuery(
            {
                siteId: props.siteId,
                incidentTime: props.incidentTime,
            },
            {
                pollingInterval: props.incidentsPollingIntervalInMs,
                refetchOnFocus: true,
            },
        );

    useEffect(() => {
        // select first incident if none selected
        if (!selectedIncidentId && data && data.length > 0) {
            setSelectedIncidentId(data[0].id);
        }
    }, [data]);

    const totalIncidentsFound = data?.length ?? 0;

    return (
        <>
            <Accordion defaultExpanded>
                <AccordionSummary
                    sx={(theme) => ({
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                    })}
                    expandIcon={
                        <ExpandMoreIcon
                            sx={(theme) => ({
                                color: theme.palette.error.contrastText,
                            })}
                        />
                    }
                >
                    <Stack
                        direction={"row"}
                        gap={2}
                    >
                        <Typography>
                            {totalIncidentsFound} incidents found for club{" "}
                            {props.siteId} at approximately{" "}
                            {DateTime.fromISO(
                                props.incidentTime,
                            ).toLocaleString(DateTime.TIME_SIMPLE)}
                        </Typography>
                        {isFetching && (
                            <CircularProgress
                                color="inherit"
                                size="20px"
                            />
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    {isError && (
                        <GlobalErrorMessage
                            title={"Unable to load incident data"}
                            message={"Please contact the service desk."}
                            debugData={error}
                            alertSeverity={"error"}
                        />
                    )}

                    {(isLoading ||
                        (isSuccess && totalIncidentsFound === 0)) && (
                        <Alert severity="warning">
                            No incidents found. Just a moment please...
                        </Alert>
                    )}

                    {isSuccess && selectedIncidentId && (
                        <Box
                            sx={{
                                flexGrow: 1,
                                bgcolor: "background.paper",
                                display: "flex",
                            }}
                        >
                            <Tabs
                                orientation="vertical"
                                variant="scrollable"
                                value={selectedIncidentId}
                                onChange={(
                                    event: React.SyntheticEvent,
                                    newValue: EntityId,
                                ) => {
                                    setSelectedIncidentId(newValue);
                                }}
                                sx={{
                                    borderRight: 1,
                                    borderColor: "divider",
                                }}
                            >
                                {data?.map((incident) => (
                                    <Tab
                                        key={`tab-${incident.id}`}
                                        id={`tab-${incident.id}`}
                                        value={incident.id}
                                        label={DateTime.fromISO(
                                            incident.timestamp,
                                        ).toLocaleString(
                                            DateTime.TIME_WITH_SECONDS,
                                        )}
                                        icon={
                                            incident.incidentType ===
                                            IncidentType.Unwell ? (
                                                <SickIcon />
                                            ) : (
                                                <SportsKabaddiIcon />
                                            )
                                        }
                                        iconPosition="start"
                                        sx={(theme) => ({
                                            paddingLeft: theme.spacing(1),
                                            paddingRight: theme.spacing(6),
                                        })}
                                    />
                                ))}
                            </Tabs>

                            {data?.map((incident) => (
                                <IncidentViewerIncidentPanel
                                    key={`tabpanel-${incident.id}`}
                                    incident={incident}
                                    show={selectedIncidentId === incident.id}
                                />
                            ))}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </>
    );
}
